import {
    useTranslate,
    IResourceComponentsProps,
    CrudFilters,
    useExport,
    useNavigation,
    HttpError,
    getDefaultFilter,
} from "@refinedev/core";

import {
    TextField,
    useTable,
    getDefaultSortOrder,
    DateField,
    NumberField,
    useSelect,
    ExportButton,
    ShowButton,
    CreateButton,
} from "@refinedev/antd";

import { SearchOutlined, FileTextOutlined, PlusOutlined, FilterOutlined } from "@ant-design/icons";

import {
    Table,
    Input,
    Form,
    DatePicker,
    Select,
    Button,
    FormProps,
    Row,
    Col,
    Space,
} from "antd";

import dayjs from "dayjs";

import { InvoiceStatus, PageHero, InitialAvatar, FilterBar } from "components";

import {
    IInvoice,
    ICustomer,
    IInvoiceFilterVariables,
    IInvoiceStatus,
} from "interfaces";
import { useMemo } from "react";

export const InvoiceList: React.FC<IResourceComponentsProps> = () => {
    const { tableProps, sorter, searchFormProps, filters } = useTable<
        IInvoice,
        HttpError,
        IInvoiceFilterVariables
    >({
        initialSorter: [{ field: "createdAt", order: "desc" }],
        onSearch: (params) => {
            const f: CrudFilters = [];
            const { q, customer, createdAt, status } = params;
            f.push({ field: "q", operator: "eq", value: q });
            f.push({ field: "customerId", operator: "eq", value: customer });
            f.push({ field: "status", operator: "in", value: status });
            f.push(
                { field: "createdAt", operator: "gte", value: createdAt ? createdAt[0].startOf("day").toISOString() : undefined },
                { field: "createdAt", operator: "lte", value: createdAt ? createdAt[1].endOf("day").toISOString() : undefined },
            );
            return f;
        },
    });

    const t = useTranslate();
    const { show } = useNavigation();

    const { isLoading, triggerExport } = useExport<IInvoice>({
        sorter,
        filters,
        pageSize: 50,
        maxItemCount: 50,
        mapData: (item) => ({ id: item.id, status: item.statusText }),
    });

    const totalCount = (tableProps?.pagination as any)?.total ?? (tableProps?.dataSource?.length ?? 0);
    const paidCount = tableProps?.dataSource?.filter((i: any) => i.statusText === "Pagamento Aprovado").length ?? 0;
    const pendingCount = tableProps?.dataSource?.filter((i: any) => i.statusText === "Aguardando Pagamento").length ?? 0;

    return (
        <>
            <PageHero
                eyebrow="Financeiro"
                title="Cobranças"
                subtitle="Acompanhe pagamentos e o split automático para os recebedores."
                icon={<FileTextOutlined />}
                tone="blue"
                actions={
                    <Space>
                        <Button onClick={triggerExport} loading={isLoading}>Exportar</Button>
                        <CreateButton type="primary" icon={<PlusOutlined />}>Criar Cobrança</CreateButton>
                    </Space>
                }
                stats={[
                    { label: "Total na página", value: totalCount },
                    { label: "Pagas", value: paidCount },
                    { label: "Aguardando", value: pendingCount },
                ]}
            />

            <FilterBar>
                <Filter formProps={searchFormProps} filters={filters || []} />
            </FilterBar>

            <div className="card-table-wrap">
                <Table
                    {...tableProps}
                    rowKey="id"
                    onRow={(record) => ({ onClick: () => show("invoices", record.id) })}
                >
                    <Table.Column
                        key="id"
                        dataIndex="id"
                        title="#"
                        width={80}
                        render={(value) => <span style={{ fontWeight: 700, color: "var(--text-secondary)" }}>#{value}</span>}
                    />
                    <Table.Column
                        key="customer.name"
                        dataIndex={["customer", "name"]}
                        title="Pagador"
                        render={(value, record: any) => (
                            <div className="cell-with-avatar">
                                <InitialAvatar name={value || record?.customer?.razaoSocial} size={38} />
                                <div className="cell-two-lines">
                                    <span className="primary">{value || "-"}</span>
                                    <span className="secondary">{record?.customer?.cpfCnpj || record?.customer?.razaoSocial || ""}</span>
                                </div>
                            </div>
                        )}
                    />
                    <Table.Column
                        key="statusText"
                        dataIndex="statusText"
                        title="Status"
                        render={(value) => <InvoiceStatus status={value} />}
                    />
                    <Table.Column
                        align="right"
                        key="gross"
                        dataIndex="gross"
                        title="Valor"
                        defaultSortOrder={getDefaultSortOrder("gross", sorter)}
                        sorter
                        render={(value) => (
                            <span style={{ fontWeight: 700, fontSize: "0.98rem", color: "var(--text-primary)", letterSpacing: "-0.01em" }}>
                                <NumberField options={{ currency: "BRL", style: "currency" }} locale="pt-BR" value={value} />
                            </span>
                        )}
                    />
                    <Table.Column
                        key="createdAt"
                        dataIndex="createdAt"
                        title="Criada em"
                        render={(value) => (
                            <span style={{ color: "var(--text-secondary)", fontSize: "0.88rem" }}>
                                <DateField value={value} format="DD/MM/YYYY HH:mm" />
                            </span>
                        )}
                        sorter
                    />
                    <Table.Column<IInvoice>
                        title=""
                        dataIndex="actions"
                        width={70}
                        render={(_, record) => (
                            <ShowButton hideText size="small" recordItemId={record.id} />
                        )}
                    />
                </Table>
            </div>
        </>
    );
};

const Filter: React.FC<{ formProps: FormProps; filters: CrudFilters }> = ({ formProps, filters }) => {
    const t = useTranslate();
    const { selectProps: customerSelectProps } = useSelect<ICustomer>({
        resource: "customers",
        optionLabel: "name",
        optionValue: "id",
        defaultValue: getDefaultFilter("customer.id", filters),
    });
    const { selectProps: invoiceSelectProps } = useSelect<IInvoiceStatus>({
        resource: "invoiceStatuses",
        optionLabel: "statusText",
        optionValue: "status",
        defaultValue: getDefaultFilter("statusText", filters),
    });
    const { RangePicker } = DatePicker;

    const createdAt = useMemo(() => {
        const start = getDefaultFilter("createdAt", filters, "gte");
        const end = getDefaultFilter("createdAt", filters, "lte");
        if (start && end) return [dayjs(start), dayjs(end)];
        return undefined;
    }, [filters]);

    return (
        <Form
            layout="vertical"
            {...formProps}
            initialValues={{
                q: getDefaultFilter("q", filters),
                customer: getDefaultFilter("customer.id", filters),
                status: getDefaultFilter("statusText", filters, "in"),
                createdAt,
            }}
        >
            <Row gutter={[12, 12]} align="bottom">
                <Col xl={7} md={12} sm={24} xs={24}>
                    <Form.Item label="Procurar" name="q">
                        <Input placeholder="Nome, CPF/CNPJ, valor..." prefix={<SearchOutlined />} />
                    </Form.Item>
                </Col>
                <Col xl={5} md={12} sm={24} xs={24}>
                    <Form.Item label="Status" name="status">
                        <Select {...invoiceSelectProps} allowClear mode="multiple" placeholder="Todos" maxTagCount="responsive" />
                    </Form.Item>
                </Col>
                <Col xl={5} md={12} sm={24} xs={24}>
                    <Form.Item label="Pagador" name="customer">
                        <Select {...customerSelectProps} allowClear placeholder="Todos" />
                    </Form.Item>
                </Col>
                <Col xl={5} md={12} sm={24} xs={24}>
                    <Form.Item label="Período" name="createdAt">
                        <RangePicker style={{ width: "100%" }} />
                    </Form.Item>
                </Col>
                <Col xl={2} md={24} sm={24} xs={24}>
                    <Form.Item label=" " colon={false}>
                        <Button htmlType="submit" type="primary" icon={<FilterOutlined />} block>
                            Filtrar
                        </Button>
                    </Form.Item>
                </Col>
            </Row>
        </Form>
    );
};
